import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Columns3,
  FolderOpen,
  Activity,
  BarChart3,
  Settings,
  Wallet,
  Wifi,
  WifiOff,
  Rocket,
  PanelLeftClose,
  PanelLeftOpen,
  Hospital,
  Film,
  Building,
  BrainCircuit,
  Globe,
} from "lucide-react";
import { SORV_PROJECTS } from "../config/sorv-projects";

const NAV_ITEMS = [
  { to: "/", icon: LayoutDashboard, label: "Mission Control" },
  { to: "/kanban", icon: Columns3, label: "Agent Board" },
  { to: "/sessions", icon: FolderOpen, label: "Sesiones" },
  { to: "/activity", icon: Activity, label: "Actividad" },
  { to: "/analytics", icon: BarChart3, label: "Metricas" },
  { to: "/finanzas", icon: Wallet, label: "Finanzas" },
  { to: "/osint", icon: Globe, label: "OSINT" },
  { to: "/settings", icon: Settings, label: "Config" },
] as const;

const PROJECT_ICONS: Record<string, typeof Hospital> = {
  hospital: Hospital,
  film: Film,
  building: Building,
  "brain-circuit": BrainCircuit,
};

const PROJECT_ROUTES: Record<string, string> = {
  "sorv-alpha": "/polymarket",
};

const STORAGE_KEY = "sidebar-collapsed";

function loadCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

interface SidebarProps {
  wsConnected: boolean;
  collapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ wsConnected, collapsed, onToggle }: SidebarProps) {
  return (
    <aside
      className={`fixed left-0 top-0 bottom-0 bg-surface-1 border-r border-border flex flex-col z-30 overflow-y-auto overflow-x-hidden transition-[width] duration-200 ${
        collapsed ? "w-[4.25rem]" : "w-60"
      }`}
    >
      {/* Brand */}
      <div className="px-3 py-4 border-b border-border">
        <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3 px-2"}`}>
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
            <Rocket className="w-4 h-4 text-accent" />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <h1 className="text-sm font-semibold text-gray-100 truncate">SORV Mission Control</h1>
              <p className="text-[11px] text-gray-500">SORV Capital</p>
            </div>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2 py-3 space-y-1">
        {NAV_ITEMS.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            title={collapsed ? label : undefined}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg text-sm font-medium transition-colors duration-150 ${
                collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
              } ${
                isActive
                  ? "bg-accent/10 text-accent border border-accent/20"
                  : "text-gray-400 hover:text-gray-200 hover:bg-surface-3 border border-transparent"
              }`
            }
          >
            <Icon className="w-4 h-4 flex-shrink-0" />
            {!collapsed && <span>{label}</span>}
          </NavLink>
        ))}

        {/* Projects section */}
        {!collapsed && (
          <div className="pt-4 mt-4 border-t border-border">
            <p className="px-3 mb-2 text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
              Proyectos
            </p>
            {SORV_PROJECTS.map((project) => {
              const Icon = (PROJECT_ICONS as Record<string, typeof Hospital>)[project.icon] ?? Activity;
              const route = PROJECT_ROUTES[project.id];
              const inner = (
                <>
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: project.color }} />
                  <span className="truncate text-xs">{project.name.split("/")[0]?.trim() ?? project.name}</span>
                  <span
                    className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full"
                    style={{
                      color: project.color,
                      backgroundColor: `${project.color}15`,
                    }}
                  >
                    {project.agents.length}
                  </span>
                </>
              );
              return route ? (
                <NavLink
                  key={project.id}
                  to={route}
                  title={project.description}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive
                        ? "text-white bg-surface-3"
                        : "text-gray-400 hover:text-gray-200 hover:bg-surface-3"
                    }`
                  }
                >
                  {inner}
                </NavLink>
              ) : (
                <div
                  key={project.id}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-gray-200 hover:bg-surface-3 transition-colors cursor-default"
                  title={project.description}
                >
                  {inner}
                </div>
              );
            })}
          </div>
        )}

        {collapsed && (
          <div className="pt-4 mt-4 border-t border-border space-y-1">
            {SORV_PROJECTS.map((project) => {
              const Icon = (PROJECT_ICONS as Record<string, typeof Hospital>)[project.icon] ?? Activity;
              const route = PROJECT_ROUTES[project.id];
              const iconEl = <Icon className="w-4 h-4" style={{ color: project.color }} />;
              return route ? (
                <NavLink
                  key={project.id}
                  to={route}
                  className="flex justify-center py-2"
                  title={project.name}
                >
                  {iconEl}
                </NavLink>
              ) : (
                <div
                  key={project.id}
                  className="flex justify-center py-2"
                  title={project.name}
                >
                  {iconEl}
                </div>
              );
            })}
          </div>
        )}
      </nav>

      {/* Collapse toggle */}
      <div className="px-2 py-2">
        <button
          onClick={onToggle}
          className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-xs text-gray-500 hover:text-gray-300 hover:bg-surface-3 transition-colors"
          title={collapsed ? "Expandir sidebar" : "Colapsar sidebar"}
        >
          {collapsed ? (
            <PanelLeftOpen className="w-4 h-4 flex-shrink-0 mx-auto" />
          ) : (
            <>
              <PanelLeftClose className="w-4 h-4 flex-shrink-0" />
              <span>Colapsar</span>
            </>
          )}
        </button>
      </div>

      {/* Footer */}
      <div
        className={`px-3 py-3 border-t border-border space-y-2 ${collapsed ? "items-center" : ""}`}
      >
        <div className={`flex items-center text-xs ${collapsed ? "justify-center" : "gap-2"}`}>
          {wsConnected ? (
            <>
              <Wifi className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
              {!collapsed && <span className="text-emerald-400">En vivo</span>}
            </>
          ) : (
            <>
              <WifiOff className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
              {!collapsed && <span className="text-gray-500">Desconectado</span>}
            </>
          )}
          {!collapsed && <span className="ml-auto text-gray-600">v1.0.0</span>}
        </div>
      </div>
    </aside>
  );
}

export { STORAGE_KEY as SIDEBAR_STORAGE_KEY, loadCollapsed };
